
'use client';

import { useAuth } from '@/hooks/useAuth';
import { collection, query, where, doc, orderBy, limit } from 'firebase/firestore';
import { Loader2, Trash2, Check, X, Handshake, FileSignature, Bot, ImageIcon } from 'lucide-react';
import { MediaUploader } from '@/components/social/MediaUploader';
import { SongPicker } from '@/components/social/SongPicker';
import { MentionTextarea } from '@/components/social/MentionTextarea';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useMemo } from 'react';
import Image from '@/components/ui/Image';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { createOrganizacionSocialPost, createSocialStory, deleteSocialPost, acceptCollaboration, rejectCollaboration, deleteContract, createContract, signContract, rejectContract, deleteSocialStory, createOrganizacion } from '@/lib/actions';
import { Organizacion, SocialPost, SocialStory, JartinetHermandad, Banda, Contract } from '@/lib/types';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { ContractCard } from '@/components/contratos/ContractCard';
import { useCollection } from '@/firebase/firestore/use-collection';
import { db } from '@/firebase/config';

function SubmitButton({ children, ...props }: { children: React.ReactNode } & React.ComponentProps<typeof Button>) {
  const [pending, setPending] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const form = formRef.current?.form;
    if (form) {
      const handleStart = () => setPending(true);
      // This is a simplified listener, might need more robust handling
      // for when the form submission actually finishes.
      return () => {
        form.removeEventListener('submit', handleStart);
      };
    }
  }, []);

  return (
    <Button type="submit" disabled={pending} ref={formRef as any} {...props}>
      {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </Button>
  );
}

function CreateContractForm({ allOrganizaciones, allHermandades, allBandas, onFormSuccess }: { allOrganizaciones: Organizacion[], allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await createContract(formData);

        if (result.success) {
          toast({ title: "Éxito", description: result.message });
          formRef.current?.reset();
          onFormSuccess();
        } else {
          toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };
  
    return (
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
        <div className="space-y-2">
            <Label htmlFor="emitterId">Emisor del Contrato</Label>
            <Select name="emitterId">
                <SelectTrigger><SelectValue placeholder="Elige una organización emisora..." /></SelectTrigger>
                <SelectContent>
                    {allOrganizaciones.map(org => (
                        <SelectItem key={`organizacion:${org.id}`} value={`organizacion:${org.id}`}>
                            <div className="flex items-center gap-2">
                                <Avatar className="h-5 w-5"><AvatarImage src={org.escudoUrl} /></Avatar>
                                {org.nombreCompleto}
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="concept">Concepto</Label>
          <Input id="concept" name="concept" placeholder="Ej: Acompañamiento Musical Domingo de Ramos" />
        </div>
         <div className="space-y-2">
          <Label htmlFor="recipientId">Destinatario</Label>
           <Select name="recipientId">
              <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
              <SelectContent>
                  {allHermandades.map(h => (
                      <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                          {h.nombreCompleto} (Hermandad)
                        </div>
                      </SelectItem>
                  ))}
                  {allBandas.map(b => (
                      <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                        <div className="flex items-center gap-2">
                           <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                           {b.nombreCompleto} (Banda)
                        </div>
                      </SelectItem>
                  ))}
              </SelectContent>
           </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="content">Contenido del Contrato</Label>
          <RichTextarea id="content" name="content" placeholder="Escribe aquí las cláusulas del contrato..." rows={10} />
           <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato (negrita, cursiva...)</p>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar Contrato
          </Button>
        </DialogFooter>
      </form>
    )
  }

function CreatePostForm({ organizaciones, allHermandades, allBandas, onFormSuccess }: { organizaciones: Organizacion[], allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await createOrganizacionSocialPost(null, formData);

        if (result.success) {
          toast({ title: "Éxito", description: result.message });
          formRef.current?.reset();
          onFormSuccess();
        } else {
          toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

  return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organizacionId">Organización</Label>
            <Select name="organizacionId">
              <SelectTrigger><SelectValue placeholder="Elige una organización..." /></SelectTrigger>
              <SelectContent>
                {organizaciones.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.nombreCompleto}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input id="title" name="title" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="content">Contenido</Label>
            <MentionTextarea id="content" name="content" placeholder="Escribe aquí el contenido... usa @ para mencionar" />
            <p className="text-xs text-muted-foreground">Usa <strong>@</strong> para mencionar cuentas. Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link>.</p>
          </div>
          <SongPicker />
          <MediaUploader label="Fotos de la publicación" max={10} />
          <div className="space-y-2">
              <Label htmlFor="collaboratorId">Invitar a colaborar (Opcional)</Label>
              <Select name="collaboratorId">
                  <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
                  <SelectContent>
                      <SelectItem value="none">No colaborar</SelectItem>
                      {allHermandades.map(h => (
                          <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                              {h.nombreCompleto} (Hermandad)
                            </div>
                          </SelectItem>
                      ))}
                      {allBandas.map(b => (
                          <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                            <div className="flex items-center gap-2">
                               <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                               {b.nombreCompleto} (Banda)
                            </div>
                          </SelectItem>
                      ))}
                  </SelectContent>
              </Select>
          </div>
           <DialogFooter>
             <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Publicar
             </Button>
          </DialogFooter>
        </form>
  );
}

function CreateStoryForm({ organizaciones, onFormSuccess }: { organizaciones: Organizacion[], onFormSuccess: () => void }) {
    const formRef = useRef<HTMLFormElement>(null);
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const organizacionId = formData.get('organizacionId') as string;
        if (!organizacionId) {
            toast({ title: "Error", description: "Debes seleccionar una organización.", variant: 'destructive' });
            return;
        }

        setIsSubmitting(true);
        const result = await createSocialStory(organizacionId, 'organizacion', formData);
  
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message || 'Ocurrió un error', variant: 'destructive' });
        }
        setIsSubmitting(false);
    }
  
    return (
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="organizacionId">Organización</Label>
          <Select name="organizacionId">
            <SelectTrigger><SelectValue placeholder="Elige una organización..." /></SelectTrigger>
            <SelectContent>
              {organizaciones.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.nombreCompleto}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <MediaUploader single required label="Foto de la historia" />
         <div className="space-y-2">
          <Label htmlFor="text">Texto (Opcional)</Label>
          <RichTextarea id="text" name="text" placeholder="Añade un texto a tu historia..." />
        </div>
        <SongPicker />
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Publicar Historia
          </Button>
        </DialogFooter>
      </form>
    );
  }

function CreateOrganizacionForm({ onFormSuccess }: { onFormSuccess: () => void }) {
    const formRef = useRef<HTMLFormElement>(null);
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        
        const data = {
            nombreCompleto: formData.get('nombreCompleto') as string,
            escudoUrl: formData.get('escudoUrl') as string,
            colorPrimario: formData.get('colorPrimario') as string,
            colorSecundario: formData.get('colorSecundario') as string,
            bio: formData.get('bio') as string,
        };

        const result = await createOrganizacion(data);
  
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message || 'Ocurrió un error', variant: 'destructive' });
        }
        setIsSubmitting(false);
    }
  
    return (
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="nombreCompleto">Nombre de la Organización</Label>
          <Input id="nombreCompleto" name="nombreCompleto" required placeholder="Ej: Consejo de Hermandades" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="escudoUrl">URL del Logo (Imgur)</Label>
          <Input id="escudoUrl" name="escudoUrl" required placeholder="https://i.imgur.com/..." />
        </div>
        <div className="grid grid-cols-2 gap-4">
             <div className="space-y-2">
                 <Label htmlFor="colorPrimario">Color Primario</Label>
                 <Input type="color" id="colorPrimario" name="colorPrimario" defaultValue="#000000" className="p-1 h-10 w-full" />
             </div>
             <div className="space-y-2">
                 <Label htmlFor="colorSecundario">Color Secundario</Label>
                 <Input type="color" id="colorSecundario" name="colorSecundario" defaultValue="#ffffff" className="p-1 h-10 w-full" />
             </div>
        </div>
         <div className="space-y-2">
          <Label htmlFor="bio">Biografía (Opcional)</Label>
          <RichTextarea id="bio" name="bio" placeholder="Información de la organización..." />
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear Organización
          </Button>
        </DialogFooter>
      </form>
    );
}

function AdminSocialManager({ posts, stories, isSubmitting, handleDeletePost, handleDeleteStory }: { posts: SocialPost[], stories: SocialStory[], isSubmitting: boolean, handleDeletePost: (postId: string) => void, handleDeleteStory: (storyId: string) => void }) {
    if (posts.length === 0 && stories.length === 0) {
        return <p className="text-muted-foreground text-sm text-center py-8">No hay contenido que gestionar.</p>
    }

    return (
        <Tabs defaultValue="posts" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="posts">Publicaciones ({posts.length})</TabsTrigger>
                <TabsTrigger value="stories">Historias ({stories.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="posts" className="space-y-4 pt-4">
                {posts.length > 0 ? posts.map(post => (
                    <div key={post.id} className="flex items-start gap-4 p-3 rounded-md border bg-muted/50">
                       <Image 
                          src={Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl || ''} 
                          alt={post.title} 
                          width={80} 
                          height={80} 
                          className="rounded-md aspect-square object-cover" 
                        />
                      <div className="flex-grow">
                        <p className="font-semibold">{post.title}</p>
                        <p className="text-xs text-muted-foreground">Por: {post.authorName}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                      </div>
                       <div className="flex flex-col gap-1">
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={isSubmitting}><Trash2 className="h-4 w-4"/></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>¿Eliminar esta publicación?</AlertDialogTitle>
                                        <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleDeletePost(post.id)} className="bg-destructive hover:bg-destructive/80">
                                          Confirmar
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                       </div>
                    </div>
                )) : <p className="text-muted-foreground text-sm text-center py-4">No hay publicaciones.</p>}
            </TabsContent>
            <TabsContent value="stories" className="space-y-2 pt-4">
                {stories.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {stories.map(story => (
                            <div key={story.id} className="relative aspect-[9/16] rounded-md overflow-hidden group">
                                <Image src={Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl} alt="Historia" className="object-cover w-full h-full" />
                                <div className="absolute inset-0 bg-black/50 flex flex-col items-start justify-between p-2">
                                  <p className="text-white text-xs font-bold">{story.authorName}</p>
                                   <div className="self-end z-20">
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                            <Button size="icon" variant="destructive" className="h-7 w-7" disabled={isSubmitting}>
                                                    <Trash2 className="h-4 w-4"/>
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>¿Eliminar esta historia?</AlertDialogTitle>
                                                    <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => handleDeleteStory(story.id)} className="bg-destructive hover:bg-destructive/80">
                                                    Confirmar
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                   </div>
                                  <p className="text-white text-xs">Expira en {Math.round((new Date(story.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))}h</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <p className="text-muted-foreground text-sm text-center py-4">No hay historias activas.</p>}
            </TabsContent>
        </Tabs>
    )
}

export default function AdminSocialPage() {
  const { user, userData, loading: authLoading, jartinetData, handleActionCompletion } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isCreateContractOpen, setCreateContractOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && userData?.role !== 'admin') {
      navigate('/jartinet');
    }
  }, [user, userData, authLoading, navigate]);
  
  // Los 400 más recientes: moderación. Antes leía la colección entera por visita ([158]).
  const postsQuery = useMemo(() => query(collection(db, 'social_posts'), orderBy('createdAt', 'desc'), limit(400)), []);
  const { data: allPosts, isLoading: isLoadingPosts } = useCollection<SocialPost>(postsQuery);

  const storiesQuery = useMemo(() => query(collection(db, 'social_stories'), where('expiresAt', '>', new Date())), []);
  const { data: allStories, isLoading: isLoadingStories } = useCollection<SocialStory>(storiesQuery);
  
  const allContractsSentQuery = useMemo(() => {
    if (!jartinetData.organizaciones) return null;
    const orgIds = jartinetData.organizaciones.map(o => `organizacion:${o.id}`);
    if (orgIds.length === 0) return null;
    return query(collection(db, 'contracts'), where('emitterId', 'in', orgIds));
  }, [jartinetData.organizaciones]);
  
  const allContractsReceivedQuery = useMemo(() => {
      if (!jartinetData.organizaciones) return null;
      const orgIds = jartinetData.organizaciones.map(o => `organizacion:${o.id}`);
      if (orgIds.length === 0) return null;
      return query(collection(db, 'contracts'), where('recipientId', 'in', orgIds));
  }, [jartinetData.organizaciones]);

  const { data: allSentContracts, isLoading: isLoadingSentContracts } = useCollection<Contract>(allContractsSentQuery);
  const { data: allReceivedContracts, isLoading: isLoadingReceivedContracts } = useCollection<Contract>(allContractsReceivedQuery);


  const allPendingCollabsQuery = useMemo(() => query(collection(db, 'social_posts'), where('collaborationStatus', '==', 'pending')), []);
  const { data: allPendingCollabs, isLoading: isLoadingPendingCollabs } = useCollection<SocialPost>(allPendingCollabsQuery);

  const orgPosts = allPosts?.filter(p => p.authorType === 'organizacion') || [];
  const orgStories = allStories?.filter(s => s.authorType === 'organizacion') || [];

  const handleGenericAction = async (action: () => Promise<any>) => {
    setIsSubmitting(true);
    try {
        const result = await action();
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            handleActionCompletion(); 
        } else {
            toast({ title: "Error", description: result.error || result.message, variant: "destructive" });
        }
    } catch (e: any) {
        toast({ title: "Error Inesperado", description: e.message, variant: "destructive" });
    } finally {
        setIsSubmitting(false);
    }
  }

  const handleDeletePost = (postId: string) => {
    const post = allPosts?.find(p => p.id === postId);
    if (!post) return;
    handleGenericAction(() => deleteSocialPost(postId, post.authorId, 'organizacion'));
  };

  const handleDeleteStory = (storyId: string) => {
    handleGenericAction(() => deleteSocialStory(storyId));
  }

  const handleDeleteContract = (contractId: string) => {
     handleGenericAction(() => deleteContract(contractId));
  }

  const handleCollaborationAction = async (action: 'accept' | 'reject', postId: string) => {
    const actionFunc = action === 'accept' ? acceptCollaboration : rejectCollaboration;
    await handleGenericAction(() => actionFunc(postId));
  };


  const isLoadingPage = authLoading || isLoadingPosts || isLoadingStories || isLoadingSentContracts || isLoadingReceivedContracts || isLoadingPendingCollabs;

  if (isLoadingPage || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos y cargando datos...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
       <div className="text-center max-w-3xl mx-auto">
        <h1 className="font-headline text-5xl font-bold text-primary">
          Gestionar Contenido Social
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Publica en nombre de las organizaciones y supervisa la actividad social del servidor.
        </p>
      </div>

      <Tabs defaultValue="social" className="w-full">
         <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="contracts">Contratos</TabsTrigger>
            <TabsTrigger value="collaborations">Colaboraciones</TabsTrigger>
        </TabsList>
        <TabsContent value="social" className="space-y-6 pt-4">
             {jartinetData.organizaciones.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No hay organizaciones</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground">No se encontraron organizaciones. Cárgalas o crea una nueva.</p>
                        <div className="mt-4">
                             <Dialog>
                                <DialogTrigger asChild><Button>Crear Organización</Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Crear Nueva Organización</DialogTitle>
                                        <DialogDescription>Añade una nueva cuenta para la red social.</DialogDescription>
                                    </DialogHeader>
                                    <CreateOrganizacionForm onFormSuccess={handleActionCompletion} />
                                </DialogContent>
                            </Dialog>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle>Acciones rápidas para Organizaciones</CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-wrap gap-4">
                            <Dialog>
                                <DialogTrigger asChild><Button variant="secondary">Crear Organización</Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Crear Nueva Organización</DialogTitle>
                                        <DialogDescription>Añade una nueva cuenta para la red social.</DialogDescription>
                                    </DialogHeader>
                                    <CreateOrganizacionForm onFormSuccess={handleActionCompletion} />
                                </DialogContent>
                            </Dialog>
                            <Dialog>
                                <DialogTrigger asChild><Button>Crear Publicación</Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Crear Publicación de Organización</DialogTitle>
                                        <DialogDescription>Publica en nombre de una organización.</DialogDescription>
                                    </DialogHeader>
                                    <CreatePostForm 
                                    organizaciones={jartinetData.organizaciones || []} 
                                    allHermandades={jartinetData.hermandades || []}
                                    allBandas={jartinetData.bandas || []}
                                    onFormSuccess={handleActionCompletion} 
                                    />
                                </DialogContent>
                            </Dialog>
                            <Dialog>
                                <DialogTrigger asChild><Button variant="outline">Crear Historia</Button></DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Crear Historia de Organización</DialogTitle>
                                        <DialogDescription>Publica una historia que durará 24h.</DialogDescription>
                                    </DialogHeader>
                                    <CreateStoryForm organizaciones={jartinetData.organizaciones || []} onFormSuccess={handleActionCompletion} />
                                </DialogContent>
                            </Dialog>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Contenido de Organizaciones</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <AdminSocialManager posts={orgPosts} stories={orgStories} isSubmitting={isSubmitting} handleDeletePost={handleDeletePost} handleDeleteStory={handleDeleteStory}/>
                        </CardContent>
                    </Card>
                </>
            )}
        </TabsContent>
        <TabsContent value="contracts" className="space-y-6 pt-4">
            <div className="flex justify-between items-center">
                <h3 className="font-headline text-3xl font-bold text-primary flex items-center gap-2"><FileSignature className="h-6 w-6"/> Contratos de Organizaciones</h3>
                <Dialog open={isCreateContractOpen} onOpenChange={setCreateContractOpen}>
                  <DialogTrigger asChild><Button size="sm">Crear Contrato</Button></DialogTrigger>
                  <DialogContent className="sm:max-w-2xl">
                      <DialogHeader>
                          <DialogTitle>Emitir Nuevo Contrato</DialogTitle>
                          <DialogDescription>Redacta un contrato para enviarlo a otra hermandad o banda.</DialogDescription>
                      </DialogHeader>
                      <CreateContractForm 
                          allOrganizaciones={jartinetData.organizaciones || []}
                          allHermandades={jartinetData.hermandades || []} 
                          allBandas={jartinetData.bandas || []}
                          onFormSuccess={handleActionCompletion}
                      />
                  </DialogContent>
              </Dialog>
            </div>
             <div className="grid md:grid-cols-2 gap-6">
                  <div>
                      <h4 className="font-semibold text-lg mb-2">Contratos Recibidos por Organizaciones</h4>
                      <div className="space-y-2">
                          {(allReceivedContracts || []).map(contract => (
                              <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50 cursor-pointer" onClick={() => setSelectedContract(contract)}>
                                  <p className="font-semibold">{contract.concept}</p>
                                  <p className="text-xs text-muted-foreground">De: {contract.emitterName}</p>
                                  <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>{contract.status.toUpperCase()}</p>
                              </div>
                          ))}
                          {(allReceivedContracts || []).length === 0 && <p className="text-xs text-muted-foreground">Ninguna organización ha recibido contratos.</p>}
                      </div>
                  </div>
                  <div>
                      <h4 className="font-semibold text-lg mb-2">Contratos Enviados por Organizaciones</h4>
                      <div className="space-y-2">
                          {(allSentContracts || []).map(contract => (
                              <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50 cursor-pointer" onClick={() => setSelectedContract(contract)}>
                                  <p className="font-semibold">{contract.concept}</p>
                                  <p className="text-xs text-muted-foreground">Para: {contract.recipientName}</p>
                                  <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>{contract.status.toUpperCase()}</p>
                              </div>
                          ))}
                          {(allSentContracts || []).length === 0 && <p className="text-xs text-muted-foreground">Ninguna organización ha enviado contratos.</p>}
                      </div>
                  </div>
              </div>

            {selectedContract && (
                <Dialog open={!!selectedContract} onOpenChange={(open) => !open && setSelectedContract(null)}>
                    <DialogContent className="w-full max-w-md h-auto max-h-[90vh] p-0">
                        <ContractCard contract={selectedContract} perspective="admin" isDetailView />
                    </DialogContent>
                </Dialog>
            )}

        </TabsContent>
         <TabsContent value="collaborations" className="space-y-4 pt-4">
          <h3 className="font-headline text-3xl font-bold text-primary flex items-center gap-2"><Handshake className="h-6 w-6"/> Invitaciones de Colaboración Pendientes</h3>
          {allPendingCollabs && allPendingCollabs.length > 0 ? (
              allPendingCollabs.map((post: SocialPost) => (
                  <div key={post.id} className="flex items-center justify-between gap-3 text-sm p-3 rounded-md bg-muted/50 border">
                      <div className="flex items-center gap-3 flex-grow overflow-hidden">
                          <Avatar className="h-10 w-10"><AvatarImage src={post.authorShieldUrl} /></Avatar>
                           <Avatar className="h-10 w-10 -ml-6 border-2 border-muted"><AvatarImage src={post.collaboratorShieldUrl} /></Avatar>
                          <div className='truncate'>
                              <p className="font-semibold truncate">{post.authorName} ha invitado a {post.collaboratorName}</p>
                              <p className="text-xs text-muted-foreground truncate">a colaborar en: "{post.title}"</p>
                          </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleCollaborationAction('reject', post.id)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-green-500" onClick={() => handleCollaborationAction('accept', post.id)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                      </div>
                  </div>
              ))
          ) : (
              <p className="text-muted-foreground text-sm text-center py-8">No hay invitaciones de colaboración pendientes.</p>
          )}
      </TabsContent>
      </Tabs>
    </div>
  );
}
